/**
 * The clinic a visitor arrived for through `/c/<slug>` — remembered for THIS
 * tab only (ZOY-614). It carries three things across the sign-in bounce:
 * the sign-in card's branding, where to continue afterwards, and — per the
 * Product recommendation on ZOY-614 — where a later sign-out returns to (the
 * URL the clinic hands its staff, not the bare root).
 *
 * It is display and preselection input, never authority: the membership check
 * happens against `/me/authz` after sign-in, and every request stays
 * server-validated.
 */

import type { ClinicEntry } from '@/core/clinic/entryApi';

const KEY = 'zc.entry';

export function rememberEntry(entry: ClinicEntry): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    /* storage unavailable — the entry flow still works, just not across the bounce */
  }
}

export function peekEntry(): ClinicEntry | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw === null) return null;
    const v = JSON.parse(raw) as Partial<ClinicEntry>;
    return typeof v.clinicId === 'string' && typeof v.slug === 'string'
      ? { clinicId: v.clinicId, slug: v.slug, displayName: v.displayName ?? null }
      : null;
  } catch {
    return null;
  }
}

export function clearEntry(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Where a signed-out tab should land: the clinic's own entry URL, else sign-in. */
export function signedOutDestination(): string {
  const e = peekEntry();
  return e ? `/c/${e.slug}` : '/signin';
}
